import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Select, MenuItem, FormControl, InputLabel, Button, Stack, Box } from '@mui/material';
import { DataContext } from '../../../../context/DataContext';
import { getToken } from '../../../../api/Account';
import {
	getGestoresAsync,
	getGestoresBySucursalAccessAsync,
	getSucursalByUserAsync
} from '../../../../api/Users';
import { getBarriosConCarteraAsync } from '../../../../api/Report';
import { getRuta, simpleMessage } from '../../../../helpers/Helpers';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { accionPrincipalSx } from '../../../../components/ui/accionPrincipal';

// 't' = "todos", igual que en el resto de los selectores de reportes.
const TODOS = 't';

const SelectorCreditsByBarrio = () => {
	const [sucursalList, setSucursalList] = useState([]);
	const [selectedSucursal, setSelectedSucursal] = useState(TODOS);

	const [gestorList, setGestorList] = useState([]);
	const [selectedGestor, setSelectedGestor] = useState(TODOS);

	const [barrioList, setBarrioList] = useState([]);
	const [selectedBarrio, setSelectedBarrio] = useState(TODOS);

	const { setIsLoading, setIsUnautorized } = useContext(DataContext);
	const ruta = getRuta();
	const token = getToken();

	// Un solo lugar donde se decide qué hacer con una respuesta fallida, en vez
	// de repetir el mismo bloque en cada carga.
	const manejarError = useCallback(
		(result) => {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
		},
		[setIsUnautorized]
	);

	const getGestores = useCallback(
		async (sucursal) => {
			const result =
				sucursal === TODOS
					? await getGestoresBySucursalAccessAsync(token)
					: await getGestoresAsync(token, sucursal);
			if (!result.statusResponse) {
				manejarError(result);
				return;
			}
			setGestorList(result.data.result);
		},
		[token, manejarError]
	);

	// Los barrios se piden al servidor ya filtrados por sucursal y gestor: así el
	// combo solo ofrece barrios que hoy tienen cartera y no hace falta la cascada
	// departamento -> municipio -> comunidad.
	const getBarrios = useCallback(
		async (sucursal, gestor) => {
			const result = await getBarriosConCarteraAsync(token, {
				sucursalId: sucursal === TODOS ? 0 : Number(sucursal),
				gestorId: gestor === TODOS ? 0 : Number(gestor),
				communityId: 0
			});
			if (!result.statusResponse) {
				manejarError(result);
				return;
			}
			setBarrioList(result.data.result);
		},
		[token, manejarError]
	);

	useEffect(() => {
		(async () => {
			setIsLoading(true);
			const result = await getSucursalByUserAsync(token);
			if (!result.statusResponse) {
				setIsLoading(false);
				manejarError(result);
				return;
			}
			setSucursalList(result.data.result);
			await getGestores(TODOS);
			await getBarrios(TODOS, TODOS);
			setIsLoading(false);
		})();
	}, [token, setIsLoading, manejarError, getGestores, getBarrios]);

	// Al cambiar sucursal se reinician gestor y barrio: los valores anteriores
	// pueden no existir en la sucursal nueva y dejarlos daría un reporte vacío.
	const onChangeSucursal = async (value) => {
		setSelectedSucursal(value);
		setSelectedGestor(TODOS);
		setSelectedBarrio(TODOS);
		setIsLoading(true);
		await getGestores(value);
		await getBarrios(value, TODOS);
		setIsLoading(false);
	};

	const onChangeGestor = async (value) => {
		setSelectedGestor(value);
		setSelectedBarrio(TODOS);
		setIsLoading(true);
		await getBarrios(selectedSucursal, value);
		setIsLoading(false);
	};

	const verReport = () => {
		const params = `${selectedSucursal}/${selectedGestor}/${selectedBarrio}`;
		window.open(`${ruta}/r-creditsbybarrio/${params}`);
	};

	return (
		<div>
			<Box sx={{ width: '100%', maxWidth: 550 }}>
				<Stack spacing={2}>
					<FormControl fullWidth style={{ textAlign: 'left' }}>
						<InputLabel id="select-sucursal-label">Sucursal</InputLabel>
						<Select
							labelId="select-sucursal-label"
							onChange={(e) => onChangeSucursal(e.target.value)}
							value={selectedSucursal}
							label="Sucursal"
						>
							{sucursalList.map((item) => (
								<MenuItem key={item.id} value={item.id}>
									{item.description}
								</MenuItem>
							))}
							<MenuItem value={TODOS}>TODAS...</MenuItem>
						</Select>
					</FormControl>

					<FormControl fullWidth style={{ textAlign: 'left' }}>
						<InputLabel id="select-gestor-label">Gestor</InputLabel>
						<Select
							labelId="select-gestor-label"
							onChange={(e) => onChangeGestor(e.target.value)}
							value={selectedGestor}
							label="Gestor"
						>
							{gestorList.map((item) => (
								<MenuItem key={item.id} value={item.id}>
									{item.fullName}
								</MenuItem>
							))}
							<MenuItem value={TODOS}>TODOS...</MenuItem>
						</Select>
					</FormControl>

					<FormControl fullWidth style={{ textAlign: 'left' }}>
						<InputLabel id="select-barrio-label">Barrio</InputLabel>
						<Select
							labelId="select-barrio-label"
							onChange={(e) => setSelectedBarrio(e.target.value)}
							value={selectedBarrio}
							label="Barrio"
						>
							{barrioList.map((item) => (
								<MenuItem key={item.id} value={item.id}>
									{item.name}
								</MenuItem>
							))}
							<MenuItem value={TODOS}>TODOS...</MenuItem>
						</Select>
					</FormControl>

					<Button
						variant="contained"
						startIcon={<FontAwesomeIcon icon={faPrint} />}
						onClick={() => verReport()}
						sx={accionPrincipalSx}
					>
						Generar Reporte
					</Button>
				</Stack>
			</Box>
		</div>
	);
};

export default SelectorCreditsByBarrio;
